<template>
  <div class="blog-page">

    <!-- ── Page Header ── -->
    <section class="page-header">
      <h1 class="page-title">Blog</h1>
      <p class="page-subtitle">
        Insights on UX Design, Research Methodologies, Human-Computer Interaction,
        and thoughts on teaching the next generation of designers.
      </p>
    </section>

    <!-- ── Loading ── -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading articles...</p>
    </div>

    <!-- ── Blog Grid ── -->
    <section v-else class="blog-section">
      <div class="blog-grid">
        <div
          v-for="(blog, index) in blogs"
          :key="index"
          class="blog-card"
        >
          <div class="blog-card-img-wrap">
            <img :src="blog.thumbnail" :alt="blog.title" class="blog-card-img" />
          </div>
          <div class="blog-card-body">
            <div class="blog-card-meta">
              <span class="blog-category-tag">{{ getCategory(blog.categories) }}</span>
            </div>
            <h3 class="blog-card-title">{{ blog.title }}</h3>
            <p class="blog-card-excerpt">{{ getExcerpt(blog.description) }}</p>
            <div class="blog-card-footer">
              <div class="blog-footer-meta">
                <span class="blog-date">{{ formatDate(blog.pubDate) }}</span>
                <span class="meta-dot">·</span>
                <span class="read-time">{{ calculateReadTime(blog.description) }} min read</span>
              </div>
              <a :href="blog.link" target="_blank" class="read-more-link">
                Read More &rarr;
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Error ── -->
    <div v-if="error" class="error-state">
      <p>{{ error }}</p>
      <button @click="fetchBlogs" class="retry-btn">Retry</button>
    </div>

  </div>
</template>

<script>
export default {
  name: 'BlogPage',
  data() {
    return {
      blogs: [],
      loading: true,
      error: null,
    };
  },
  methods: {
    async fetchBlogs() {
      this.loading = true;
      this.error = null;
      try {
        const response = await fetch(
          'https://api.rss2json.com/v1/api.json?rss_url=https://medium.com/feed/@ravindrabachate'
        );
        const data = await response.json();
        if (data.status === 'ok') {
          this.blogs = data.items.map(post => ({
            ...post,
            thumbnail: this.extractThumbnail(post.content) || require('@/assets/RB.png'),
          }));
        } else {
          throw new Error('Failed to fetch');
        }
      } catch {
        this.error = 'Failed to load blog posts. Please try again later.';
      } finally {
        this.loading = false;
      }
    },
    extractThumbnail(content) {
      const match = content.match(/<img[^>]+src="([^">]+)"/);
      return match ? match[1] : null;
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
    getExcerpt(description) {
      const plainText = description.replace(/<[^>]*>/g, '');
      return plainText.length > 160 ? plainText.substring(0, 160) + '...' : plainText;
    },
    calculateReadTime(description) {
      const wordCount = description.replace(/<[^>]*>/g, '').split(/\s+/).length;
      const t = Math.ceil(wordCount / 200);
      return t < 1 ? 1 : t;
    },
    getCategory(categories) {
      const map = {
        'ux-design': 'UX Design',
        'user-research': 'Research',
        'artificial-intelligence': 'AI & ML',
        'design-process': 'Design Process',
        'case-study': 'Case Studies',
        'teaching': 'Teaching',
        'accessibility': 'Accessibility',
        'hci': 'HCI',
      };
      if (!categories || !categories.length) return 'UX Design';
      const match = categories.find(c => map[c.toLowerCase()]);
      return match ? map[match.toLowerCase()] : 'UX Design';
    },
  },
  mounted() {
    this.fetchBlogs();
  },
};
</script>

<style scoped>
/* ── Page ── */
.blog-page {
  background: var(--color-bg-white);
  min-height: 100vh;
  overflow-x: hidden;
}

/* ── Header ── */
.page-header {
  text-align: center;
  padding: 7rem var(--space-6) var(--space-12);
  background: var(--color-bg-light);
  border-bottom: 1px solid var(--color-bg-subtle);
}
/* .page-title and .page-subtitle come from global style.css */

/* ── Grid ── */
.blog-section {
  max-width: var(--container-xl);
  margin: 0 auto;
  padding: var(--space-16) var(--space-6) var(--section-py);
}
.blog-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-8);
}

/* ── Card ── */
.blog-card {
  background: var(--color-bg-white);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  overflow: hidden;
  box-shadow: var(--shadow-xs);
  display: flex;
  flex-direction: column;
  transition: box-shadow var(--duration-base) var(--ease-out), transform var(--duration-base) var(--ease-out);
}
.blog-card:hover {
  box-shadow: var(--shadow-xl);
  transform: translateY(-4px);
}

.blog-card-img-wrap {
  width: 100%;
  height: 210px;
  overflow: hidden;
  background: var(--color-bg-subtle);
  display: flex;
  align-items: center;
  justify-content: center;
}
.blog-card-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  transition: transform var(--duration-slow) var(--ease-out);
}
.blog-card:hover .blog-card-img {
  transform: scale(1.04);
}

.blog-card-body {
  padding: var(--space-6);
  display: flex;
  flex-direction: column;
  flex: 1;
}

.blog-card-meta {
  margin-bottom: 0.75rem;
}
.blog-category-tag {
  display: inline-block;
  font-size: var(--text-xs);
  font-weight: var(--font-semibold);
  color: var(--color-primary);
  background: var(--color-primary-subtle);
  border: 1px solid var(--color-primary-border);
  border-radius: var(--radius-full);
  padding: 0.2rem 0.75rem;
}

.blog-card-title {
  font-size: var(--text-base);
  font-weight: var(--font-bold);
  color: var(--color-bg-darker);
  line-height: var(--leading-snug);
  margin: 0 0 0.65rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.blog-card-excerpt {
  font-size: var(--text-sm);
  color: var(--color-text-400);
  line-height: var(--leading-relaxed);
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: var(--space-5);
}

.blog-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
}
.blog-footer-meta {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: var(--text-xs);
  color: var(--color-text-300);
}
.meta-dot { color: var(--color-border-strong); }

.read-more-link {
  font-size: var(--text-sm);
  font-weight: var(--font-semibold);
  color: var(--color-primary);
  text-decoration: none;
  transition: color var(--duration-base) var(--ease-out);
}
.read-more-link:hover { color: var(--color-primary-dark); }

/* ── States ── */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6rem var(--space-6);
  color: var(--color-text-300);
}
.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid var(--color-bg-subtle);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: var(--space-4);
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-state {
  text-align: center;
  padding: var(--space-16) var(--space-6);
  color: var(--color-text-400);
}
.retry-btn {
  margin-top: var(--space-4);
  padding: 0.6rem var(--space-6);
  background: var(--color-primary);
  color: var(--color-bg-white);
  border: none;
  border-radius: var(--radius-full);
  font-weight: var(--font-semibold);
  cursor: pointer;
  transition: background var(--duration-base) var(--ease-out);
}
.retry-btn:hover { background: var(--color-primary-dark); }

/* ── Responsive ── */
@media (max-width: 1024px) {
  .blog-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 640px) {
  .blog-grid { grid-template-columns: 1fr; }
  .page-header { padding: 6rem var(--space-4) var(--space-10); }
  .blog-section { padding: var(--space-10) var(--space-4) var(--space-16); }
}
</style>
